/**
 * Auth API - User authentication endpoints
 *
 * Fetches user information from the /api/v1/me endpoint.
 * When running behind Pomerium, returns authenticated user info.
 * When running locally without Pomerium, returns authenticated: false.
 */
import { apiClient } from './client';

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  groups?: string[];
  roles?: Array<{
    name: string;
    source: string;
    scope?: string;
  }>;
  permissions?: {
    isAdmin?: boolean;
    isSuperAdmin?: boolean;
    global?: Record<string, string[]>;
  };
  /**
   * RFC3339 timestamp of when the current session JWT expires, surfaced by
   * compute-api's /me so the UI can proactively refresh before then
   * (PLT-8655). Absent when the auth path carries no JWT exp.
   */
  sessionExpiresAt?: string;
}

export interface MeResponse {
  authenticated: boolean;
  user?: AuthUser;
}

/**
 * Fetch current user information from /api/v1/me
 * Returns authentication status and user details if authenticated.
 *
 * `/me` is the auth probe — a 401 here is the answer to "am I authorised?",
 * not a stale-token signal. Skipping the global refresh + session-expired
 * overlay lets AuthProvider classify 401 (no workspace access) and 403
 * cleanly into the AccessDeniedScreen instead of competing with overlay UI.
 */
export async function getMe(): Promise<MeResponse> {
  return apiClient.get<MeResponse>('/api/v1/me', { skipAuthRetry: true });
}

/**
 * Update the currently signed-in user's profile.
 * Backend accepts a partial payload; today only `name` is editable.
 */
export async function patchMe(data: { name: string }): Promise<AuthUser> {
  return apiClient.patch<AuthUser>('/api/v1/me', data);
}
